import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { 
  Check, 
  AlertCircle, 
  Eye, 
  EyeOff, 
  Building, 
  ArrowRight,
  Mail,
  Lock,
  User,
  Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

// Form validation schema
const signupSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain an uppercase letter')
    .regex(/[a-z]/, 'Password must contain a lowercase letter')
    .regex(/[0-9]/, 'Password must contain a number')
    .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain a special character'),
  confirmPassword: z.string(),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  organizationName: z.string().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type FormData = z.infer<typeof signupSchema>;

export default function StreamlinedSignup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signUp } = useAuth();
  
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<any>(null);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [rateLimitSeconds, setRateLimitSeconds] = useState(0);
  
  const invitationToken = searchParams.get('invitation'); // Primary method - matches email URL
  const invitationId = searchParams.get('token'); // Legacy support for old links
  
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(signupSchema),
    mode: 'onChange',
  });
  
  const watchPassword = watch('password');
  
  // Load invitation details if present
  useEffect(() => {
    if (invitationToken || invitationId) {
      loadInvitation();
    }
  }, [invitationToken, invitationId]);
  
  // Calculate password strength
  useEffect(() => {
    if (!watchPassword) {
      setPasswordStrength(0);
      return;
    }
    
    let strength = 0;
    if (watchPassword.length >= 8) strength += 20;
    if (watchPassword.length >= 12) strength += 20;
    if (/[A-Z]/.test(watchPassword)) strength += 20;
    if (/[a-z]/.test(watchPassword)) strength += 20;
    if (/[0-9]/.test(watchPassword)) strength += 20;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(watchPassword)) strength += 20;
    
    setPasswordStrength(Math.min(100, strength));
  }, [watchPassword]);
  
  const loadInvitation = async () => {
    try {
      const invitationUuid = invitationToken || invitationId;
      
      if (!invitationUuid) return;
      
      const { data, error } = await supabase
        .from('user_provisioning_requests')
        .select('*, organizations(name)')
        .eq('id', invitationUuid)
        .eq('status', 'approved')
        .is('provisioned_at', null)
        .maybeSingle();
      
      if (error) {
        throw new Error('Failed to load invitation');
      }
      
      if (!data) {
        // Check if invitation exists but is already provisioned or not approved
        const { data: used } = await supabase
          .from('user_provisioning_requests')
          .select('provisioned_at, status, created_at')
          .eq('id', invitationUuid)
          .maybeSingle();
          
        if (used?.provisioned_at) {
          throw new Error('This invitation has already been used. Please request a new invitation.');
        }
        if (used?.status !== 'approved') {
          throw new Error('This invitation is still pending approval.');
        }
        throw new Error('Invitation not found or has expired.');
      }
      
      // Check expiration (7 days from created_at)
      const createdDate = new Date(data.created_at);
      const expiryDate = new Date(createdDate);
      expiryDate.setDate(expiryDate.getDate() + 7);
      
      if (expiryDate < new Date()) {
        throw new Error('This invitation has expired. Invitations are valid for 7 days.');
      }
      
      setInvitation(data);
      setValue('email', data.email, { shouldValidate: true, shouldDirty: false });
      setValue('firstName', data.first_name || '', { shouldValidate: true });
      setValue('lastName', data.last_name || '', { shouldValidate: true });
      setValue('organizationName', data.organizations?.name || '', { shouldValidate: true, shouldDirty: false });
    } catch (error: any) {
      console.error('Failed to load invitation:', error);
      setError(error.message || 'Invalid or expired invitation link');
    }
  };
  
  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    setError(null);
    
    // Prevent tampering with invitation-locked fields
    if (invitation) {
      if (data.email !== invitation.email) {
        setError('Email cannot be changed for invited users');
        setIsLoading(false);
        return;
      }
      if (data.organizationName !== invitation.organizations?.name) {
        setError('Organization cannot be changed for invited users');
        setIsLoading(false);
        return;
      }
    }
    
    try {
      const result = await signUp(
        data.email,
        data.password,
        data.firstName,
        data.lastName,
        data.organizationName,
        invitation?.id || undefined
      );
      
      if (result.error) {
        const errorMsg = result.error.message || result.error || 'Registration failed';
        
        // Check for rate limit errors
        if (errorMsg.includes('rate limit') || errorMsg.includes('60 seconds') || errorMsg.includes('Too many')) {
          setError('⏱️ Too many attempts. Please wait 60 seconds before trying again.');
          setRateLimitSeconds(60);
          const interval = setInterval(() => {
            setRateLimitSeconds(prev => {
              if (prev <= 1) {
                clearInterval(interval);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        } 
        // Check for existing user
        else if (errorMsg.includes('already exists')) {
          setError('An account with this email already exists. Please try signing in instead.');
        } 
        // Generic error
        else {
          setError(errorMsg);
        }
        return;
      }
      
      // Check if email verification is required
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // No session means email verification is required - show verification screen
        setEmailVerificationSent(true);
      } else {
        // Session exists - immediately sign in the user to establish proper session
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: data.email,
          password: data.password
        });
        
        if (signInError) {
          setError('Account created but could not sign in. Please try logging in manually.');
          return;
        }
        
        // Wait briefly for session to fully establish
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Navigate to onboarding with new session
        navigate('/onboarding');
      }
    } catch (error: any) {
      setError(error.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Step indicator component
  const StepIndicator = () => (
    <div className="flex justify-center mb-8">
      <div className="flex items-center space-x-4">
        {[1, 2, 3].map((num) => (
          <React.Fragment key={num}>
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ 
                scale: step >= num ? 1 : 0.8,
              }}
              className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold transition-colors ${
                step >= num ? 'bg-primary' : 'bg-muted'
              }`}
            >
              {step > num ? <Check size={20} /> : num}
            </motion.div>
            {num < 3 && (
              <div 
                className={`w-16 h-1 transition-colors ${step > num ? 'bg-primary' : 'bg-muted'}`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
  
  // Email verification success screen
  if (emailVerificationSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/10 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <Card>
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="text-primary" size={32} />
              </div>
              <CardTitle className="text-2xl">Check Your Email</CardTitle>
              <CardDescription className="text-base mt-2">
                We've sent a verification link to <strong>{watch('email')}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Click the link in your email to verify your account and get started.
                  The link will expire in 24 hours.
                </AlertDescription>
              </Alert>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/auth')}
              >
                Back to Sign In
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/10 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl w-full"
      >
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2">
            {invitation ? `Join ${invitation.organizations?.name || 'Organization'}` : 'Create Your Account'}
          </h1>
          <p className="text-muted-foreground">
            {invitation 
              ? `You've been invited to join as ${invitation.requested_roles?.join(', ') || 'member'}`
              : 'Start managing your camp meals in minutes'
            }
          </p>
        </div>
        
        <StepIndicator />
        
        <Card>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <AnimatePresence mode="wait">
                {/* Step 1: Basic Info */}
                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-4"
                  >
                    {invitation && (
                      <Alert className="mb-4">
                        <Building className="h-4 w-4" />
                        <AlertDescription>
                          <strong>Joining by Invitation</strong>
                          <p className="text-sm mt-1">
                            You're being added to <strong>{invitation.organizations?.name || 'Organization'}</strong>
                            {invitation.requested_roles && ` as ${invitation.requested_roles.join(', ')}`}
                          </p>
                        </AlertDescription>
                      </Alert>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">
                          <User className="inline w-4 h-4 mr-1" />
                          First Name
                        </Label>
                        <Input
                          id="firstName"
                          {...register('firstName')}
                          placeholder="John"
                          disabled={invitation?.first_name}
                        />
                        {errors.firstName && (
                          <p className="text-destructive text-sm mt-1">{errors.firstName.message}</p>
                        )}
                      </div>
                      
                      <div>
                        <Label htmlFor="lastName">
                          <User className="inline w-4 h-4 mr-1" />
                          Last Name
                        </Label>
                        <Input
                          id="lastName"
                          {...register('lastName')}
                          placeholder="Doe"
                          disabled={invitation?.last_name}
                        />
                        {errors.lastName && (
                          <p className="text-destructive text-sm mt-1">{errors.lastName.message}</p>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="email">
                        <Mail className="inline w-4 h-4 mr-1" />
                        Email Address
                        {invitation && (
                          <Lock className="inline w-3 h-3 ml-1 text-muted-foreground" />
                        )}
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        {...register('email')}
                        placeholder="john@example.com"
                        readOnly={!!invitation}
                        disabled={!!invitation}
                        className={invitation ? "bg-muted cursor-not-allowed" : ""}
                      />
                      {errors.email && (
                        <p className="text-destructive text-sm mt-1">{errors.email.message}</p>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="organizationName">
                        <Building className="inline w-4 h-4 mr-1" />
                        Organization Name
                        {invitation && (
                          <Lock className="inline w-3 h-3 ml-1 text-muted-foreground" />
                        )}
                      </Label>
                      <Input
                        id="organizationName"
                        {...register('organizationName')}
                        placeholder={invitation ? invitation.organization_name : "Camp Sunshine"}
                        readOnly={!!invitation}
                        disabled={!!invitation}
                        className={invitation ? "bg-muted cursor-not-allowed" : ""}
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        {invitation 
                          ? "You're joining this organization via invitation" 
                          : "The name of your camp or organization"
                        }
                      </p>
                    </div>
                    
                    <Button
                      type="button"
                      className="w-full"
                      onClick={() => setStep(2)}
                      disabled={!watch('firstName') || !watch('lastName') || !watch('email')}
                    >
                      Continue
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </motion.div>
                )}
                
                {/* Step 2: Password */}
                {step === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-4"
                  >
                    <div>
                      <Label htmlFor="password">
                        <Lock className="inline w-4 h-4 mr-1" />
                        Password
                      </Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          {...register('password')}
                          placeholder="Enter a strong password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2"
                        >
                          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                      
                      {/* Password strength indicator */}
                      {watchPassword && (
                        <div className="mt-2">
                          <div className="flex justify-between text-xs mb-1">
                            <span>Password strength</span>
                            <span className={
                              passwordStrength < 40 ? 'text-destructive' :
                              passwordStrength < 70 ? 'text-yellow-500' :
                              'text-green-500'
                            }>
                              {passwordStrength < 40 ? 'Weak' :
                               passwordStrength < 70 ? 'Medium' :
                               'Strong'}
                            </span>
                          </div>
                          <Progress value={passwordStrength} className="h-2" />
                        </div>
                      )}
                      
                      {errors.password && (
                        <p className="text-destructive text-sm mt-1">{errors.password.message}</p>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="confirmPassword">
                        <Lock className="inline w-4 h-4 mr-1" />
                        Confirm Password
                      </Label>
                      <Input
                        id="confirmPassword"
                        type={showPassword ? 'text' : 'password'}
                        {...register('confirmPassword')}
                        placeholder="Enter password again"
                      />
                      {errors.confirmPassword && (
                        <p className="text-destructive text-sm mt-1">{errors.confirmPassword.message}</p>
                      )}
                    </div>
                    
                    <div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
                      <p className="text-sm font-medium mb-2">Password Requirements:</p>
                      <ul className="text-xs space-y-1">
                        <li className={watchPassword?.length >= 8 ? 'text-green-600' : 'text-muted-foreground'}>
                          ✓ At least 8 characters
                        </li>
                        <li className={/[A-Z]/.test(watchPassword || '') ? 'text-green-600' : 'text-muted-foreground'}>
                          ✓ One uppercase letter
                        </li>
                        <li className={/[a-z]/.test(watchPassword || '') ? 'text-green-600' : 'text-muted-foreground'}>
                          ✓ One lowercase letter
                        </li>
                        <li className={/[0-9]/.test(watchPassword || '') ? 'text-green-600' : 'text-muted-foreground'}>
                          ✓ One number
                        </li>
                        <li className={/[!@#$%^&*(),.?":{}|<>]/.test(watchPassword || '') ? 'text-green-600' : 'text-muted-foreground'}>
                          ✓ One special character
                        </li>
                      </ul>
                    </div>
                    
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1"
                        onClick={() => setStep(1)}
                      >
                        Back
                      </Button>
                      <Button
                        type="button"
                        className="flex-1"
                        onClick={() => setStep(3)}
                        disabled={!isValid || passwordStrength < 60}
                      >
                        Continue
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </motion.div>
                )}
                
                {/* Step 3: Review & Submit */}
                {step === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-4"
                  >
                    <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                      <h3 className="font-semibold mb-3">
                        Review Your Information
                      </h3>
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <dt className="text-muted-foreground">Name:</dt>
                          <dd className="font-medium">
                            {watch('firstName')} {watch('lastName')}
                          </dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-muted-foreground">Email:</dt>
                          <dd className="font-medium">{watch('email')}</dd>
                        </div>
                        {watch('organizationName') && (
                          <div className="flex justify-between">
                            <dt className="text-muted-foreground">Organization:</dt>
                            <dd className="font-medium">{watch('organizationName')}</dd>
                          </div>
                        )}
                        {invitation && (
                          <div className="flex justify-between">
                            <dt className="text-muted-foreground">Role:</dt>
                            <dd className="font-medium capitalize">
                              {invitation.requested_roles?.join(', ')}
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>
                    
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {error}
                    {rateLimitSeconds > 0 && (
                      <div className="mt-2 font-semibold">
                        Try again in {rateLimitSeconds} seconds...
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}
                    
                    <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                      <h4 className="font-semibold mb-2 flex items-center">
                        <Sparkles className="mr-2 h-4 w-4" />
                        What happens next?
                      </h4>
                      <ul className="text-sm space-y-1">
                        <li>• We'll send you a verification email</li>
                        <li>• Click the link to activate your account</li>
                        <li>• Complete a quick setup wizard</li>
                        <li>• Start planning your camp meals!</li>
                      </ul>
                    </div>
                    
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1"
                        onClick={() => setStep(2)}
                        disabled={isLoading}
                      >
                        Back
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1"
                        disabled={isLoading || !isValid || rateLimitSeconds > 0}
                      >
                        {isLoading ? (
                          'Creating Account...'
                        ) : rateLimitSeconds > 0 ? (
                          `Wait ${rateLimitSeconds}s...`
                        ) : (
                          'Create Account'
                        )}
                      </Button>
                    </div>
                    
                    <p className="text-center text-sm text-muted-foreground">
                      By creating an account, you agree to our{' '}
                      <a href="/terms" className="text-primary hover:underline">
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a href="/privacy" className="text-primary hover:underline">
                        Privacy Policy
                      </a>
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
          </CardContent>
        </Card>
        
        <p className="text-center text-sm text-muted-foreground mt-6">
          Already have an account?{' '}
          <a href="/auth" className="text-primary hover:underline font-medium">
            Sign in
          </a>
        </p>
      </motion.div>
    </div>
  );
}
